import { useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { authService } from "../../services";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const navigate = useNavigate();
  const hasChecked = useRef(false);

  // Check auth status once and memoize
  const isAuthenticated = useMemo(() => authService.isAuthenticated(), []);
  const isRegistrationComplete = useMemo(() => isAuthenticated && authService.isCreatorRegistrationComplete(), [isAuthenticated]);

  useEffect(() => {
    // Only check once
    if (hasChecked.current) return;
    hasChecked.current = true;

    // Redirect if not authenticated
    if (!isAuthenticated) {
      navigate("/auth/intro", { replace: true });
      return;
    }

    // Redirect if registration incomplete
    if (!isRegistrationComplete) {
      const decodedToken = authService.getDecodedToken();
      if (decodedToken.sourceId) {
        navigate(`/auth/signUp?id=${decodedToken.sourceId}&email=${decodedToken.email || ""}&currentTab=creator`, {
          replace: true,
        });
      } else {
        navigate("/auth/signUp?currentTab=creator", { replace: true });
      }
    }
  }, []); // Empty deps - only run once

  // Don't render children if authentication checks fail
  if (!isAuthenticated || !isRegistrationComplete) {
    return null;
  }

  // User is authenticated and registration complete
  return <>{children}</>;
};

export default ProtectedRoute;
